import React, { useState } from 'react';
import GridCell from './GridCell';
import DefinitionPopover from './DefinitionPopover';

export default function Grid({
  puzzle,
  grid,
  onSubmitWord,
  onRetry,
  canRetry,
  CELL_STATES,
  focusedCell,
  onCellFocus,
}) {
  const { rowAdjectives, colAdjectives, definitions } = puzzle;
  const [activeDefinition, setActiveDefinition] = useState(null);

  const toggleDefinition = (key) => {
    setActiveDefinition((prev) => (prev === key ? null : key));
  };

  return (
    <div className="inline-block max-w-full">
      <table className="border-collapse border-2 border-ink w-full sm:w-auto">
        <thead>
          <tr>
            <th className="w-[20%] sm:w-20 md:w-24 h-12 border border-ink bg-parchment" />
            {colAdjectives.map((adj, i) => (
              <th
                key={i}
                className="sm:w-28 md:w-36 h-12 text-center text-[10px] sm:text-xs md:text-sm font-serif font-bold text-ink border border-ink bg-parchment px-1 sm:px-2 relative"
              >
                <div className="flex flex-col items-center">
                  <span>{adj}</span>
                  <button
                    onClick={() => toggleDefinition(`col-${i}`)}
                    className="text-ink-faint hover:text-ink text-xs cursor-pointer"
                    aria-label={`Definition of ${adj}`}
                    aria-expanded={activeDefinition === `col-${i}`}
                  >
                    &#9432;
                  </button>
                </div>
                {activeDefinition === `col-${i}` && (
                  <DefinitionPopover
                    word={adj}
                    definition={definitions?.[adj]}
                    onClose={() => setActiveDefinition(null)}
                    position="below"
                  />
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rowAdjectives.map((rowAdj, rowIndex) => (
            <tr key={rowIndex}>
              <th className="w-[20%] sm:w-20 md:w-24 h-20 sm:h-28 md:h-36 text-[10px] sm:text-xs md:text-sm font-serif font-bold text-ink bg-parchment border border-ink px-1 sm:px-2 text-center relative">
                <div className="flex flex-col items-center justify-center">
                  <span>{rowAdj}</span>
                  <button
                    onClick={() => toggleDefinition(`row-${rowIndex}`)}
                    className="text-ink-faint hover:text-ink text-xs cursor-pointer mt-1"
                    aria-label={`Definition of ${rowAdj}`}
                    aria-expanded={activeDefinition === `row-${rowIndex}`}
                  >
                    &#9432;
                  </button>
                </div>
                {activeDefinition === `row-${rowIndex}` && (
                  <DefinitionPopover
                    word={rowAdj}
                    definition={definitions?.[rowAdj]}
                    onClose={() => setActiveDefinition(null)}
                    position="right"
                  />
                )}
              </th>
              {colAdjectives.map((colAdj, colIndex) => (
                <td
                  key={colIndex}
                  className="sm:w-28 md:w-36 h-20 sm:h-28 md:h-36 border border-ink bg-white align-middle"
                >
                  <GridCell
                    cell={grid[rowIndex][colIndex]}
                    rowAdj={rowAdj}
                    colAdj={colAdj}
                    onSubmit={(word, cellState) => onSubmitWord(rowIndex, colIndex, word, cellState)}
                    onRetry={() => onRetry(rowIndex, colIndex)}
                    canRetry={canRetry}
                    CELL_STATES={CELL_STATES}
                    isFocusTarget={focusedCell?.row === rowIndex && focusedCell?.col === colIndex}
                    onFocus={() => onCellFocus({ row: rowIndex, col: colIndex })}
                    grid={grid}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
